import { Controller } from "@hotwired/stimulus";
import suneditor from "suneditor";
import { blockquote, blockStyle, codeBlock, hr, image, link, list, table } from "suneditor/plugins";
import { converter, markdown } from "suneditor/src/helper/index.js";

export default class extends Controller {
  static targets = ["bodyTxt", "surfaceEl"];

  static editorPlugins = {
    blockquote,
    blockStyle,
    codeBlock,
    hr,
    image,
    link,
    list,
    table,
  };

  // Mounts SunEditor with its built-in toolbar and image dialog.
  // Called by Stimulus when the document editor frame connects.
  // Existing Markdown is converted to HTML for SunEditor display.
  connect() {
    this.mount();
  }

  // Creates the editor and configures Rails-backed image uploads.
  // Called by connect after a document frame is inserted or restored.
  // Uploaded images are inserted by SunEditor from the Rails JSON response.
  mount() {
    if (this.editor) return;

    this.currentMarkdown = this.bodyTxtTarget.value || "";
    this.currentHtml = markdown.markdownToHtml(this.currentMarkdown);
    const imageUploadUrl = this.imageUploadUrl;

    this.editor = suneditor.create(this.surfaceElTarget, {
      plugins: this.constructor.editorPlugins,
      theme: "dark",
      value: this.currentHtml,
      width: "100%",
      minHeight: "26rem",
      resizingBar: true,
      image: {
        createUrlInput: false,
        uploadUrl: imageUploadUrl,
        uploadHeaders: { "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
        uploadSingleSizeLimit: 10 * 1024 * 1024,
        acceptedFormats: ".jpg,.jpeg,.png,.webp,.gif",
      },
      buttonList: [
        [
          "undo",
          "redo",
          "blockStyle",
          "bold",
          "italic",
          "underline",
          "strike",
          "list",
          "outdent",
          "indent",
          "blockquote",
          "codeBlock",
          "table",
          "link",
          "image",
          "hr",
          "preview",
          "codeView",
          "removeFormat",
          "markdownView",
        ],
      ],
      events: {
        onChange: ({ data }) => this.syncMarkdown(data),
        onImageUploadBefore: ({ info }) => this.insertUploadedImages(info),
      },
    });

    this.bodyTxtTarget.hidden = true;
    this.surfaceElTarget.hidden = false;
    this.form = this.element.closest("form");
    this.handleSubmit = () => this.syncBeforeSubmit();
    this.form?.addEventListener("submit", this.handleSubmit);
    this.syncMarkdown();
  }

  // Returns the Rails endpoint used by SunEditor’s image dialog.
  // Called while mounting SunEditor for a new or persisted CompanyDocument.
  // New documents upload to a temporary endpoint until the record is created.
  get imageUploadUrl() {
    const documentId = this.element.closest("turbo-frame")?.dataset.documentId;
    return documentId ? `/company_documents/${documentId}/content_images` : "/company_documents/staged_content_images";
  }

  // Keeps an uploaded image as a new editor item when another image is selected.
  // Called by SunEditor through onImageUploadBefore before its upload response is handled.
  // SunEditor otherwise interprets the upload as replacing the currently selected image.
  insertUploadedImages(info) {
    if (info.files?.length) {
      // Make SunEditor's response handler insert a new image instead of changing the selected image's src.
      info.isUpdate = false;
    }

    return true;
  }

  // Reports typing in SunEditor's rich text, Markdown, and code views immediately.
  // Called by input->company-document-editor#contentInput on the editor wrapper.
  contentInput(event) {
    if (!event.target.closest(".sun-editor")) return;

    this.dispatch("input");
  }

  // Converts SunEditor HTML back into the Markdown field Rails persists.
  // Called by SunEditor onChange and before the form submits.
  // Image URLs and alt text remain stable Markdown references.
  syncMarkdown(contents = this.currentHtml) {
    if (!this.editor) return;
    this.currentHtml = contents;
    this.bodyTxtTarget.value = this.markdownFromHtml(this.currentHtml);
    this.dispatch("change");
  }

  // Synchronizes the editor immediately before Rails serializes the form.
  // Called by the company-document-content wrapper on submit->company-document-editor#syncBeforeSubmit.
  // Ensures the hidden body_markdown textarea contains the latest editor content.
  syncBeforeSubmit() {
    if (!this.editor) return;

    const editable = this.element.querySelector(".sun-editor-editable");
    const frameEditable = this.editor.$?.frameContext?.get("wysiwyg");
    const currentHtml = editable?.innerHTML || frameEditable?.innerHTML || this.currentHtml;
    this.currentHtml = currentHtml;
    this.bodyTxtTarget.value = this.markdownFromHtml(currentHtml);
  }

  // Converts SunEditor’s current HTML DOM into the Markdown stored by Rails.
  // Called by syncMarkdown and syncBeforeSubmit at the persistence boundary.
  // Returns Markdown with stable image URLs and alt text preserved.
  markdownFromHtml(html) {
    this.currentMarkdown = markdown.jsonToMarkdown(converter.htmlToJson(html || ""));
    return this.currentMarkdown;
  }

  // Cleans up SunEditor before Turbo caches or removes the editor frame.
  // Called by turbo:before-cache@document->company-document-editor#teardown and disconnect.
  // Restores the textarea so the no-JavaScript fallback remains usable.
  teardown() {
    if (!this.editor) return;
    this.syncMarkdown();
    this.form?.removeEventListener("submit", this.handleSubmit);
    this.form = null;
    this.handleSubmit = null;
    this.editor.destroy();
    this.editor = null;
    this.bodyTxtTarget.hidden = false;
    this.surfaceElTarget.replaceChildren();
  }

  // Releases the SunEditor instance when Turbo replaces the document frame.
  // Called by Stimulus when the editor controller disconnects.
  // All editor event handlers and DOM are removed by destroy().
  disconnect() {
    this.teardown();
  }
}
